/**
 * The inspection tools, as a disclosure rather than as more toolbar.
 *
 * Closed by default and closed on first visit. Somebody who opened their model
 * to look at it never has to see a section-plane slider; somebody checking one
 * finds all five tools in one place instead of hunting a ribbon.
 *
 * The grid spacing is stated, not implied. A grid whose squares are an unnamed
 * size is decoration; one labelled "1 kare = 0,05 birim" is a ruler. Where the
 * model's scale has been calibrated the caller passes the unit and the label
 * says millimetres - and where it has not, the label says "birim", because
 * printing a millimetre figure for an uncalibrated solve would be inventing a
 * measurement.
 */

import { useId, useState } from "react";

import { CLIP_AXES, type ClipAxis, type InspectionState } from "./viewerInspection";

/** The customer words for each section axis. */
const AXIS_LABELS: Record<ClipAxis, string> = {
  x: "Soldan sağa",
  y: "Alttan üste",
  z: "Önden arkaya",
};

export function ViewerInspector({
  state,
  onChange,
  gridSpacing,
  gridUnit,
  wireframeRefusal,
}: {
  state: InspectionState;
  onChange: (next: InspectionState) => void;
  /** The grid's square size in model units, or null before the mesh loads. */
  gridSpacing: number | null;
  /**
   * What one model unit is called. "mm" once a scale has been confirmed;
   * undefined while it has not, and then no physical unit is claimed.
   */
  gridUnit?: string | undefined;
  /** Why no wireframe is available, when there is a reason. */
  wireframeRefusal: string | null;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  const toggle = (key: keyof InspectionState) => () =>
    onChange({ ...state, [key]: !state[key] } as InspectionState);

  const spacingLabel =
    gridSpacing === null
      ? null
      : gridUnit
        ? `1 kare = ${formatSpacing(gridSpacing)} ${gridUnit}`
        : `1 kare = ${formatSpacing(gridSpacing)} birim (ölçek doğrulanmadı)`;

  return (
    <div className="viewer__inspect">
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
      >
        İnceleme araçları
      </button>
      {open && (
        <div id={panelId} className="viewer__inspect-panel">
          <fieldset className="viewer__inspect-group">
            <legend>Görünenler</legend>
            <label className="inline-field">
              <input
                type="checkbox"
                checked={state.wireframe}
                disabled={wireframeRefusal !== null}
                onChange={toggle("wireframe")}
              />
              <span>Tel kafes</span>
            </label>
            <label className="inline-field">
              <input type="checkbox" checked={state.grid} onChange={toggle("grid")} />
              <span>Zemin ızgarası</span>
            </label>
            <label className="inline-field">
              <input type="checkbox" checked={state.axes} onChange={toggle("axes")} />
              <span>Eksenler</span>
            </label>
            <label className="inline-field">
              <input
                type="checkbox"
                checked={state.boundingBox}
                onChange={toggle("boundingBox")}
              />
              <span>Sınır kutusu</span>
            </label>
            {wireframeRefusal && <p className="muted">{wireframeRefusal}</p>}
            {state.grid && spacingLabel && <p className="muted">{spacingLabel}</p>}
          </fieldset>

          <fieldset className="viewer__inspect-group">
            <legend>Kesit</legend>
            <label className="inline-field">
              <input
                type="checkbox"
                checked={state.clipping}
                onChange={toggle("clipping")}
              />
              <span>Kesit düzlemi</span>
            </label>
            {state.clipping && (
              <>
                <label className="inline-field">
                  <span>Yön</span>
                  <select
                    value={state.clipAxis}
                    onChange={(event) =>
                      onChange({ ...state, clipAxis: event.target.value as ClipAxis })
                    }
                    aria-label="Kesit yönü"
                  >
                    {CLIP_AXES.map((axis) => (
                      <option key={axis} value={axis}>
                        {AXIS_LABELS[axis]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="inline-field">
                  <span>Konum</span>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.005}
                    value={state.clipPosition}
                    aria-label="Kesit konumu"
                    onChange={(event) =>
                      onChange({ ...state, clipPosition: Number(event.target.value) })
                    }
                  />
                </label>
                <label className="inline-field">
                  <input
                    type="checkbox"
                    checked={state.clipFlipped}
                    onChange={toggle("clipFlipped")}
                  />
                  <span>Diğer yarısını göster</span>
                </label>
              </>
            )}
            {/*
              Said plainly, because the opposite belief is the dangerous one:
              somebody who thinks a section changed their model would export it
              expecting half a mesh.
            */}
            <p className="muted">
              Kesit yalnızca bu görünümü etkiler. İndirilen model tam haliyle kalır.
            </p>
          </fieldset>
        </div>
      )}
    </div>
  );
}

/** Trailing zeroes removed, comma as the decimal mark, Turkish convention. */
function formatSpacing(value: number): string {
  const digits = value >= 1 ? 2 : Math.min(6, Math.max(2, -Math.floor(Math.log10(value)) + 2));
  return value.toLocaleString("tr-TR", { maximumFractionDigits: digits });
}
